import { styled } from "@mui/material/styles";
import {
    InternalStandardProps,
    Grid,
    Box,
    ListItem,
    ListItemButton,
    CircularProgress,
    Typography,
    Dialog,
    DialogContent,
    DialogProps,
    IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppTypography } from "../AppTypography";
import { AppSearchBar } from "../AppSearchBar";
import { AppCheckboxList } from "../AppCheckboxList";
import { Members } from "JS/Models";
import { Icon, Icons } from "../Icon";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    boxShadow: `0px -8px 10px ${theme.palette.grey[200]}`,
    minWidth: "100%",
    minHeight: `calc(95vh - (${theme.footer.height} + ${theme.header.height} + 30px))`,
    border: `1px solid ${theme.palette.grey[200]}`,
}));

const DialogContentWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
}));

const MainContent = styled(DialogContent)(() => ({
    padding: "0 !important",
}));

const TopContent = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const ContentHeaderItem = styled(Grid)(() => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const MembersWrapper = styled("span")(({ theme }) => ({
    background: theme.palette.grey[900],
    color: theme.palette.grey[200],
    borderRadius: "50%",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5),
    width: "30px",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    cursor: "pointer",
    color: theme.palette.grey[600],
    fontSize: "22px",
}));

const BtnOkWrapper = styled(Grid)(() => ({
    display: "flex",
    justifyContent: "center",
}));

const BtnOkay = styled(ListItemButton)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey[200]}`,
    color: theme.palette.common.black,
}));

const WrapperChkBoxList = styled(Grid)(({ theme }) => ({
    overflow: "auto",
    height: `calc(100vh - (${theme.footer.height} + ${theme.header.height} + 200px))`,
    backgroundColor: theme.palette.background.default,
}));

const Spinner = styled(CircularProgress)(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const Grey600Typography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const SingleSelectText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
    marginLeft: "10px",
    fontSize: "14px",
}));

const HiddenSearchBar = styled(AppSearchBar)(() => ({
    visibility: "hidden",
}));

export interface SelectMembersDialogProps
    extends InternalStandardProps<DialogProps> {
    onClose: () => void;
    onSend?: () => void;
    selectedMembers: Members[];
    checkboxList?: Members[];
    searchable: boolean;
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    onToggleItemSelect: (value: string) => void;
    searchPlaceholder: string;
    giftSendLoading?: boolean;
    availableQuantity?: number;
    singleSelect?: boolean;
}

export const SelectMembersDialog = (props: SelectMembersDialogProps) => {
    const {
        className,
        searchable,
        setSearchQuery,
        searchPlaceholder,
        searchQuery,
        onClose,
        checkboxList,
        selectedMembers,
        onToggleItemSelect,
        giftSendLoading,
        availableQuantity,
        singleSelect,
        onSend,
        ...rest
    } = props;

    return (
        <StyledDialog fullWidth className={className} {...rest}>
            <DialogContentWrapper>
                <MainContent>
                    <TopContent container>
                        <ContentHeaderItem item xs={12}>
                            {singleSelect ? (
                                <Box
                                    display={"flex"}
                                    justifyContent={"flex-start"}
                                    alignItems={"center"}
                                >
                                    <Icon icon={Icons.alertIcon} />
                                    <SingleSelectText variant="body1">
                                        Select One Member Only
                                    </SingleSelectText>
                                </Box>
                            ) : (
                                <Box
                                    display={"flex"}
                                    justifyContent={"flex-start"}
                                    alignItems={"center"}
                                >
                                    {availableQuantity ||
                                    availableQuantity === 0 ? (
                                        <MembersWrapper>
                                            {availableQuantity > 99
                                                ? "99+"
                                                : availableQuantity}
                                        </MembersWrapper>
                                    ) : (
                                        <MembersWrapper>
                                            {selectedMembers &&
                                                selectedMembers.length}
                                        </MembersWrapper>
                                    )}
                                    <Grey600Typography
                                        justifySelf={"center"}
                                        variant="body1"
                                    >
                                        Available to Send
                                    </Grey600Typography>
                                </Box>
                            )}
                            <IconButton aria-label="Close" onClick={onClose}>
                                <StyledIcon icon={["fas", "xmark"]} />
                            </IconButton>
                        </ContentHeaderItem>
                        <Grid item xs={12}>
                            {searchable ? (
                                <AppSearchBar
                                    value={searchQuery}
                                    onChange={(e) => {
                                        if (setSearchQuery) {
                                            setSearchQuery(e.target.value);
                                        }
                                    }}
                                    placeholder={searchPlaceholder}
                                    elementId="select-member-search"
                                    onCancel={() => {
                                        if (setSearchQuery) {
                                            setSearchQuery("");
                                        }
                                    }}
                                />
                            ) : (
                                <HiddenSearchBar
                                    value={searchQuery}
                                    onChange={(e) => {
                                        if (setSearchQuery) {
                                            setSearchQuery(e.target.value);
                                        }
                                    }}
                                    placeholder={searchPlaceholder}
                                    elementId="select-member-search"
                                    onCancel={() => {
                                        if (setSearchQuery) {
                                            setSearchQuery("");
                                        }
                                    }}
                                />
                            )}
                        </Grid>
                        <WrapperChkBoxList item xs={12}>
                            <AppCheckboxList
                                checkboxList={checkboxList}
                                selectedMembers={selectedMembers}
                                onToggleItemSelect={onToggleItemSelect}
                            />
                        </WrapperChkBoxList>
                    </TopContent>
                    <Grid container>
                        <BtnOkWrapper item xs={12}>
                            <ListItem key="btnOk" disablePadding>
                                <BtnOkay
                                    disabled={giftSendLoading}
                                    onClick={onSend}
                                >
                                    {!giftSendLoading ? (
                                        <Typography variant="body1">
                                            SEND
                                        </Typography>
                                    ) : (
                                        <Spinner />
                                    )}
                                </BtnOkay>
                            </ListItem>
                        </BtnOkWrapper>
                    </Grid>
                </MainContent>
            </DialogContentWrapper>
        </StyledDialog>
    );
};
